import { useReducer, useCallback } from 'react';
import {
  FormData,
  WizardSelection,
  WizardStepType,
  WizardState,
  STEP_ORDER,
  STEP_LABELS,
  WizardStepData,
} from '@/components/wizard/types';
import { API } from '@/utils/API';

interface State extends WizardState {
  formData: FormData | null;
  isRefetching: boolean;
  /**
   * OV-1180: which sub-category the Variant step is currently asking about. Option A
   * asks the variant question once per selected sub-category that has variants, so the
   * single Variant slot in STEP_ORDER walks a queue instead of being asked once.
   */
  variantQueueIndex: number;
}

type Action =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_REFETCHING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FORM_DATA'; payload: FormData }
  | { type: 'UPDATE_SELECTION'; payload: Partial<WizardSelection> }
  | { type: 'SET_STEP'; payload: WizardStepType }
  | { type: 'SET_VARIANT_INDEX'; payload: number }
  | { type: 'RESET' };

const initialState: State = {
  currentStep: WizardStepType.CATEGORY,
  selection: {},
  selectedForm: null,
  isLoading: true,
  error: null,
  formData: null,
  isRefetching: false,
  variantQueueIndex: 0,
};

const wizardReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_REFETCHING':
      return { ...state, isRefetching: action.payload };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isRefetching: false,
      };
    case 'SET_FORM_DATA':
      return {
        ...state,
        formData: action.payload,
        isLoading: false,
        isRefetching: false,
        error: null,
      };
    case 'UPDATE_SELECTION':
      return { ...state, selection: { ...state.selection, ...action.payload } };
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_VARIANT_INDEX':
      return { ...state, variantQueueIndex: action.payload };
    case 'RESET':
      return { ...initialState, formData: state.formData, isLoading: false };
    default:
      return state;
  }
};

const fetchFormData = async (ownerId?: string): Promise<FormData> => {
  const params = new URLSearchParams();
  if (ownerId) params.append('owner_id', ownerId);

  const url = `/submissions/new${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await API.authenticated.fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch form data');
  }
  return response.json();
};

const variantKey = (category: string, subCategory: string) =>
  `${category}::${subCategory}`;

/** Empty value for a step's selection, matching that step's shape. */
const clearedValueFor = (step: WizardStepType) => {
  if (step === WizardStepType.SUBCATEGORY) return [];
  if (step === WizardStepType.VARIANT) return {};
  return null;
};

export const usePreFormWizard = () => {
  const [state, dispatch] = useReducer(wizardReducer, initialState);

  const selectedSubCategories = (selection: WizardSelection): string[] =>
    selection.subCategory ?? [];

  /** Does this category's Sub-Category step accept several picks? */
  const isMultiSelectCategory = (category?: string): boolean =>
    !!category && !!state.formData?.multi_select?.categories.includes(category);

  /** Sub-categories that must be submitted on their own, for this category. */
  const exclusiveSubCategories = (category?: string): string[] =>
    (category &&
      state.formData?.multi_select?.exclusive_sub_categories?.[category]) ||
    [];

  /**
   * Sorts a selection into the order the backend listed the sub-categories in, so the
   * selection never depends on which box the user clicked first.
   */
  const inCanonicalOrder = (
    category: string | undefined,
    picked: string[]
  ): string[] => {
    const ordered = (category && state.formData?.subcategories[category]) || [];
    const known = ordered.filter(subCategory => picked.includes(subCategory));
    // Anything the backend did not list (shouldn't happen) keeps its relative order.
    const unknown = picked.filter(
      subCategory => !ordered.includes(subCategory)
    );

    return [...known, ...unknown];
  };

  /**
   * The sub-categories picked so far that have a variant to ask about, in the order
   * the backend listed them. This is the queue the Variant step walks.
   */
  const variantQueue = (selection: WizardSelection): string[] => {
    const { category } = selection;
    if (!category || !state.formData) return [];

    const ordered = state.formData.subcategories[category] ?? [];
    const picked = selectedSubCategories(selection);

    return ordered
      .filter(subCategory => picked.includes(subCategory))
      .filter(
        subCategory =>
          (state.formData?.variants[variantKey(category, subCategory)] ?? [])
            .length > 0
      );
  };

  const stepOptions = (
    step: WizardStepType,
    selectionOverride?: WizardSelection,
    variantIndexOverride?: number
  ): string[] => {
    if (!state.formData) return [];
    const selection = selectionOverride || state.selection;

    switch (step) {
      case WizardStepType.CATEGORY:
        return state.formData.categories;

      case WizardStepType.SUBCATEGORY:
        if (!selection.category) return [];
        return state.formData.subcategories[selection.category] ?? [];

      case WizardStepType.VARIANT: {
        if (!selection.category) return [];

        const queue = variantQueue(selection);
        const index = variantIndexOverride ?? state.variantQueueIndex;
        const subCategory = queue[index];
        if (!subCategory) return [];

        return (
          state.formData.variants[
            variantKey(selection.category, subCategory)
          ] ?? []
        );
      }

      default:
        return [];
    }
  };

  /**
   * Picking an exclusive sub-category (Translation, Appeal) rules out everything else,
   * and picking anything else rules those out. Advisory only — the backend rejects an
   * illegal combination regardless.
   */
  const disabledSubCategories = (
    selection: WizardSelection
  ): { options: string[]; byExclusiveSelection: boolean } => {
    const none = { options: [], byExclusiveSelection: false };
    const { category } = selection;
    if (!isMultiSelectCategory(category)) return none;

    const exclusive = exclusiveSubCategories(category);
    const picked = selectedSubCategories(selection);
    if (picked.length === 0) return none;

    // An exclusive option is already picked, so everything else is ruled out —
    // the opposite reason from the branch below, and it needs the opposite wording.
    if (picked.some(value => exclusive.includes(value))) {
      const options = stepOptions(WizardStepType.SUBCATEGORY, selection);
      return {
        options: options.filter(option => !picked.includes(option)),
        byExclusiveSelection: true,
      };
    }

    return { options: exclusive, byExclusiveSelection: false };
  };

  const currentStepData = (): WizardStepData => {
    const { currentStep, selection } = state;
    const allowMultiple =
      currentStep === WizardStepType.SUBCATEGORY &&
      isMultiSelectCategory(selection.category);

    const queue = variantQueue(selection);
    const variantForSubCategory =
      currentStep === WizardStepType.VARIANT
        ? queue[state.variantQueueIndex]
        : undefined;

    let selected: string[] = [];
    if (currentStep === WizardStepType.SUBCATEGORY) {
      selected = selectedSubCategories(selection);
    } else if (currentStep === WizardStepType.CATEGORY && selection.category) {
      selected = [selection.category];
    } else if (variantForSubCategory) {
      const chosen = selection.variant?.[variantForSubCategory];
      selected = chosen ? [chosen] : [];
    }

    // Name the sub-category when several variant screens follow one another, so it is
    // clear which one is being asked about.
    const title =
      variantForSubCategory && queue.length > 1
        ? `Select Variant — ${variantForSubCategory}`
        : STEP_LABELS[currentStep];

    const disabled =
      currentStep === WizardStepType.SUBCATEGORY
        ? disabledSubCategories(selection)
        : { options: [], byExclusiveSelection: false };

    return {
      currentStep,
      title,
      options: stepOptions(currentStep),
      allowMultiple,
      selected,
      disabledOptions: disabled.options,
      disabledByExclusiveSelection: disabled.byExclusiveSelection,
      variantForSubCategory,
    };
  };

  const initializeForm = useCallback(
    async (ownerId?: string) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const data = await fetchFormData(ownerId);
        dispatch({ type: 'SET_FORM_DATA', payload: data });
      } catch (_error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load form data' });
      }
    },
    [dispatch]
  );

  const nextStep = (selectionOverride?: WizardSelection) => {
    const selection = selectionOverride || state.selection;

    // Walk the variant queue before leaving the Variant step.
    if (state.currentStep === WizardStepType.VARIANT) {
      const queue = variantQueue(selection);
      const nextIndex = state.variantQueueIndex + 1;

      if (
        nextIndex < queue.length &&
        stepOptions(WizardStepType.VARIANT, selection, nextIndex).length > 0
      ) {
        dispatch({ type: 'SET_VARIANT_INDEX', payload: nextIndex });
        return;
      }

      dispatch({ type: 'SET_STEP', payload: WizardStepType.REVIEW });
      return;
    }

    const next = STEP_ORDER[STEP_ORDER.indexOf(state.currentStep) + 1];
    if (!next) return;

    if (next === WizardStepType.VARIANT) {
      // Entering the queue: start at the first sub-category that has variants.
      dispatch({ type: 'SET_VARIANT_INDEX', payload: 0 });
      if (stepOptions(WizardStepType.VARIANT, selection, 0).length > 0) {
        dispatch({ type: 'SET_STEP', payload: WizardStepType.VARIANT });
      } else {
        dispatch({ type: 'SET_STEP', payload: WizardStepType.REVIEW });
      }
      return;
    }

    if (stepOptions(next, selection).length > 0) {
      dispatch({ type: 'SET_STEP', payload: next });
    } else {
      dispatch({ type: 'SET_STEP', payload: WizardStepType.REVIEW });
    }
  };

  // lockedSteps lets a wrapper (e.g. PreFormWizardWithCategory) that auto-selects
  // and hides a step also keep Back from walking into it — without this, a step
  // hidden going forward (via hiddenSteps on WizardStep/ReviewStep) is still fully
  // reachable and editable going backward, since clearing its selection here would
  // otherwise re-expose it as a normal, currently-selectable step.
  //
  // Returns whether it actually navigated to an earlier step. When every step
  // behind the current one is either empty (no options for this owner/category —
  // e.g. a category with no variant options at all) or locked, there's nowhere
  // left to land — the caller should treat this as "the wizard has no more
  // steps to show" and fall back to leaving the wizard entirely, rather than
  // silently doing nothing.
  const previousStep = (lockedSteps: WizardStepType[] = []): boolean => {
    const queue = variantQueue(state.selection);

    // From Review, step back into the last variant screen rather than past the queue,
    // clearing its answer so it can be re-picked (see the queue-walk below).
    if (state.currentStep === WizardStepType.REVIEW && queue.length > 0) {
      const lastIndex = queue.length - 1;
      const subCategory = queue[lastIndex];
      if (subCategory) {
        const remaining = { ...(state.selection.variant ?? {}) };
        delete remaining[subCategory];
        dispatch({ type: 'UPDATE_SELECTION', payload: { variant: remaining } });
      }
      dispatch({ type: 'SET_VARIANT_INDEX', payload: lastIndex });
      dispatch({ type: 'SET_STEP', payload: WizardStepType.VARIANT });
      return true;
    }

    // Walk back through the variant queue one sub-category at a time, clearing the
    // answer for the screen being returned TO — the same convention clearedValueFor
    // applies to every other step. Clearing the screen being left instead would show
    // the earlier screen already answered, and since a single-select step has no
    // Continue button and re-clicking a checked radio fires no change event, the user
    // would have no way forward.
    if (
      state.currentStep === WizardStepType.VARIANT &&
      state.variantQueueIndex > 0
    ) {
      const previousIndex = state.variantQueueIndex - 1;
      const subCategory = queue[previousIndex];
      if (subCategory) {
        const remaining = { ...(state.selection.variant ?? {}) };
        delete remaining[subCategory];
        dispatch({ type: 'UPDATE_SELECTION', payload: { variant: remaining } });
      }
      dispatch({ type: 'SET_VARIANT_INDEX', payload: previousIndex });
      return true;
    }

    dispatch({
      type: 'UPDATE_SELECTION',
      payload: { [state.currentStep]: clearedValueFor(state.currentStep) },
    });

    let target = STEP_ORDER[STEP_ORDER.indexOf(state.currentStep) - 1];

    // Skip steps with no options, and steps the caller has locked.
    //
    // A locked step is one whose answer was derived rather than chosen, so
    // stepping back past it also CLEARS it: the derivation belongs to the
    // choice the user is now going back to change, and leaving the stale value
    // behind means a later return to that step finds it already answered. That
    // is what made re-picking the same category after Back land on the raw
    // picker instead of re-deriving (OV-1358).
    while (
      target &&
      (stepOptions(target).length === 0 || lockedSteps.includes(target))
    ) {
      if (lockedSteps.includes(target)) {
        dispatch({
          type: 'UPDATE_SELECTION',
          payload: { [target]: clearedValueFor(target) },
        });
      }
      target = STEP_ORDER[STEP_ORDER.indexOf(target) - 1];
    }

    if (target && stepOptions(target).length > 0) {
      dispatch({ type: 'SET_STEP', payload: target });
      return true;
    }

    return false;
  };

  const selectOption = (option: string) => {
    const { currentStep, selection } = state;
    const stepData = currentStepData();

    if (stepData.disabledOptions?.includes(option)) return;

    let payload: Partial<WizardSelection>;

    if (currentStep === WizardStepType.SUBCATEGORY) {
      const picked = selectedSubCategories(selection);

      if (!stepData.allowMultiple) {
        payload = { subCategory: [option] };
      } else if (picked.includes(option)) {
        payload = { subCategory: picked.filter(value => value !== option) };
      } else if (exclusiveSubCategories(selection.category).includes(option)) {
        // Translation/Appeal stand alone — picking one drops everything else.
        payload = { subCategory: [option] };
      } else {
        // Held in the backend's canonical order, not click order. The first entry
        // becomes the submission's primary template — the one that supplies its
        // category, sub_category and title — so ticking the same boxes in a different
        // order must not produce a differently-titled submission.
        payload = {
          subCategory: inCanonicalOrder(selection.category, [
            ...picked,
            option,
          ]),
        };
      }
    } else if (currentStep === WizardStepType.VARIANT) {
      const subCategory = stepData.variantForSubCategory;
      if (!subCategory) return;
      payload = {
        variant: { ...(selection.variant ?? {}), [subCategory]: option },
      };
    } else {
      // Changing category invalidates everything chosen beneath it.
      payload = { category: option, subCategory: [], variant: {} };
    }

    const updatedSelection = { ...selection, ...payload };
    dispatch({ type: 'UPDATE_SELECTION', payload });

    // Auto-advance for single-select steps only. On a multi-select step the user keeps
    // ticking boxes and presses Next themselves.
    if (currentStep !== WizardStepType.REVIEW && !stepData.allowMultiple) {
      nextStep(updatedSelection);
    }
  };

  const reset = () => {
    dispatch({ type: 'RESET' });
  };

  return {
    currentStepData: currentStepData(),
    isLoading: state.isLoading,
    isRefetching: state.isRefetching,
    error: state.error,
    selection: state.selection,
    owner: state.formData?.owner,
    blockedMessage: state.formData?.blocked?.message ?? null,
    hasNoCategories: !!state.formData && state.formData.categories.length === 0,

    // Actions
    selectOption,
    nextStep,
    previousStep,
    reset,
    initializeForm,
  };
};
